import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Package, TrendingDown, Warehouse, History } from "lucide-react";
import ProductosPage from "@/pages/ProductosPage";
import PanelStockPage from "@/pages/PanelStockPage";
import InventarioValorizadoPage from "@/pages/InventarioValorizadoPage";
import HistorialVentasPage from "@/pages/HistorialVentasPage";
import { useAuth, useHasPerm } from "@/context/AuthContext";

/**
 * Hub `/productos` con tabs:
 *   - Catálogo (siempre)
 *   - Bajo stock (director + `inventario.ajustar` / `productos.editar`)
 *   - Inventario valorizado (director + `productos.ver_costo` / `reportes.ver_financieros`)
 *   - Historial de ventas (director + `ventas.ver_todos` / `productos.ver_costo` / `reportes.ver_financieros`)
 */
export default function ProductosHubPage() {
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const canAjustar = useHasPerm("inventario.ajustar");
  const canEditar = useHasPerm("productos.editar");
  const canVerCosto = useHasPerm("productos.ver_costo");
  const canVerFin = useHasPerm("reportes.ver_financieros");
  const canVerVentas = useHasPerm("ventas.ver_todos");

  const TABS = useMemo(() => [
    { key: "catalogo", label: "Catálogo", icon: Package, Component: ProductosPage, testid: "tab-catalogo", visible: true },
    { key: "bajo-stock", label: "Bajo stock", icon: TrendingDown, Component: PanelStockPage, testid: "tab-bajo-stock", visible: isDirector || canAjustar || canEditar },
    { key: "inventario", label: "Inventario", icon: Warehouse, Component: InventarioValorizadoPage, testid: "tab-inventario", visible: isDirector || canVerCosto || canVerFin },
    { key: "historial", label: "Historial de ventas", icon: History, Component: HistorialVentasPage, testid: "tab-historial", visible: isDirector || canVerVentas || canVerCosto || canVerFin },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [isDirector, canAjustar, canEditar, canVerCosto, canVerFin, canVerVentas]);

  const visibles = TABS.filter((t) => t.visible);
  const [sp, setSp] = useSearchParams();
  const requested = sp.get("tab");
  const active = visibles.find((t) => t.key === requested) ? requested : visibles[0]?.key || "catalogo";
  const Active = visibles.find((t) => t.key === active);
  const setTab = (k) => {
    const n = new URLSearchParams(sp);
    n.set("tab", k);
    setSp(n, { replace: true });
  };

  return (
    <div>
      <div className="border-b border-border mb-6">
        <div className="flex gap-1 overflow-x-auto -mb-px" role="tablist" aria-label="Secciones de Productos">
          {visibles.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setTab(t.key)}
                data-testid={t.testid}
                className={`px-4 h-11 flex items-center gap-2 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div data-testid={`productos-tab-content-${active}`} className="hub-embedded">
        {Active && <Active.Component embedded />}
      </div>
    </div>
  );
}
